import React from 'react';

export type ToastTone = 'success' | 'error' | 'info';

export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type Toast = {
  id: string;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
};

const DURATION_BY_TONE: Record<ToastTone, number> = {
  success: 4500,
  info: 4500,
  error: 8000,
};

let toastCounter = 0;

/** Kurzlebige Statusmeldungen ohne globalen Store — bewusst pro Seite gehalten. */
export function useToasts() {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismissToast = React.useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = React.useCallback(
    (message: string, tone: ToastTone = 'success', action?: ToastAction) => {
      const id = `toast-${++toastCounter}`;
      setToasts((prev) => [...prev, { id, message, tone, action }]);
      timers.current.set(
        id,
        setTimeout(() => dismissToast(id), DURATION_BY_TONE[tone]),
      );
      return id;
    },
    [dismissToast],
  );

  React.useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return { toasts, pushToast, dismissToast };
}
